import { useEffect, useState } from "react";
import { ExternalLink, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Providers from "@/pages/Providers";
import Fleet from "@/pages/Fleet";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function BusinessCompanies() {
  const [company, setCompany] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api.get("/companies/mine/info").then((r) => setCompany(r.data)).catch(() => setLoadError(true));
  }, []);

  const hubUrl = company ? `${window.location.origin}/hub/${company.slug}` : "";

  const copyUrl = () => {
    navigator.clipboard.writeText(hubUrl);
    toast.success("Booking URL copied");
  };

  return (
    <div data-testid="business-companies-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Companies</h1>
        <p className="mt-1 text-sm text-muted-foreground">Outside vendors you can hire from, and your own fleet with its public booking link.</p>
      </div>

      <Tabs defaultValue="external">
        <TabsList className="bg-muted" data-testid="companies-tabs">
          <TabsTrigger value="external" data-testid="tab-external-vendors">External Vendors</TabsTrigger>
          <TabsTrigger value="fleet" data-testid="tab-my-fleet">My Fleet</TabsTrigger>
        </TabsList>

        <TabsContent value="external" className="mt-4">
          <p className="mb-4 border border-border bg-white px-4 py-3 text-xs text-muted-foreground" data-testid="external-vendors-note">
            CRM directory only — store who has what, rates and contacts, then call or email them manually for availability. No public pages or slugs are created for outside vendors.
          </p>
          <Providers />
        </TabsContent>

        <TabsContent value="fleet" className="mt-4 space-y-4">
          <div className="border border-border bg-white p-5" data-testid="booking-url-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your public booking URL</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <code className="bg-muted px-3 py-2 font-mono text-sm font-semibold text-brand" data-testid="booking-url">{hubUrl || (loadError ? "Could not load hub URL — check Hub Page settings" : "…")}</code>
              <Button data-testid="copy-booking-url" size="sm" variant="outline" className="rounded-md" onClick={copyUrl}>
                <Link2 className="mr-1.5 h-3.5 w-3.5" /> Copy
              </Button>
              {company && (
                <a data-testid="open-booking-url" href={hubUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm font-semibold text-brand hover:text-brand-dark">
                  Open <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              One URL handles all booking enquiries — the form checks your diary live: dates where every active unit is booked are auto-blocked, and requests beyond your fleet's total capacity are flagged so you know partner units are needed.
            </p>
          </div>
          <Fleet />
        </TabsContent>
      </Tabs>
    </div>
  );
}
